// Pure helpers for Labour Advance Generation (MOID 702) - kept out of the page so they can be unit tested.

export const ADVANCE_CATEGORIES = ['SK', 'SSK', 'USK', 'HSK'];

const norm = (k) => String(k).toLowerCase().replace(/[\s_]/g, '');

// Sheet rows (XLSX sheet_to_json) -> UploadStaging Rows.
// Columns: LabourId, Name (or LabourName), Category, Days Present (or DaysWorked / Days).
// Days are cumulative for the month from day 1 (week 1 = 6, week 2 = 12 ...).
// Returns { rows, error } - error is the first problem found, rows is empty then.
export const parseAdvanceSheetRows = (sheetRows, month, year) => {
    if (!sheetRows?.length) return { rows: [], error: 'Excel sheet is empty' };
    const daysInMonth = new Date(year, month, 0).getDate();
    const rows = [];

    for (let i = 0; i < sheetRows.length; i++) {
        const n = {};
        Object.keys(sheetRows[i]).forEach((k) => { n[norm(k)] = sheetRows[i][k]; });

        const labourId = String(n.labourid ?? '').trim();
        if (!labourId) continue;                      // blank / total lines

        const line = i + 2;                           // Excel row number (row 1 = headings)
        const category = String(n.category ?? '').trim().toUpperCase();
        if (!ADVANCE_CATEGORIES.includes(category)) {
            return { rows: [], error: `Row ${line}: Invalid Category "${category}". Must be SK/SSK/USK/HSK` };
        }
        const rawDays = n.dayspresent ?? n.daysworked ?? n.days ?? '';
        const days = parseFloat(rawDays);
        if (String(rawDays).trim() === '' || Number.isNaN(days) || days <= 0 || days > daysInMonth) {
            return { rows: [], error: `Row ${line}: Days Present must be more than 0 and not more than ${daysInMonth}` };
        }
        rows.push({
            LabourId:   labourId,
            LabourName: String(n.name ?? n.labourname ?? '').trim(),
            Category:   category,
            DaysWorked: Math.round(days * 10) / 10,
        });
    }
    if (!rows.length) return { rows: [], error: 'No labour rows found in the sheet' };
    return { rows, error: '' };
};

// Checks one entered amount against the preview row. Blank / 0 = no advance for that labour.
export const advanceRowError = (row, value) => {
    const s = String(value ?? '').trim();
    if (s === '') return '';
    const amt = Number(s);
    if (Number.isNaN(amt) || amt < 0) return 'Invalid amount';
    if (amt === 0) return '';
    if (row.ValidationStatus !== 'Valid') return row.ValidationStatus;
    if (amt > (Number(row.AvailableAmount) || 0)) return 'More than available';
    return '';
};

// Generate payload; only rows with an amount > 0 are sent (backend skips 0 anyway).
export const buildGenerateAdvancePayload = ({ form, uploadBatchId, previewRows, amounts, roleId, createdBy }) => ({
    CCCode:        form.ccCode,
    AdvanceMonth:  parseInt(form.month, 10),
    AdvanceYear:   parseInt(form.year, 10),
    UploadBatchId: uploadBatchId,
    AdvanceDate:   form.advanceDate || null,
    RoleId:        roleId || 0,
    CreatedBy:     createdBy || '',
    Amounts: previewRows
        .map((r) => ({ LabourId: r.LabourId, AdvanceAmount: Number(amounts[r.LabourId]) || 0 }))
        .filter((a) => a.AdvanceAmount > 0),
});

export const isOwnLabour = (r) => !r.LabourType || r.LabourType === 'Own Labour';

// InboxRouter: spInsertNotification sends 702 rows with path /HR/VerifyLabourAdvance or /HR/ApproveLabourAdvance
// and category 'Labour Advance'. Anchored so the HR Salary Advance catch-all ("advance") never takes these,
// and the category is matched as a whole so "labour advance payment ..." style names do not.
export const isLabourAdvanceVerificationRoute = (path = '', category = '') =>
    /\/hr\/(verify|approve)labouradvance(?:$|[?#/])/.test(String(path).toLowerCase()) ||
    String(category).trim().toLowerCase() === 'labour advance';

// Approve payload = ApproveAdvanceDomain. The SP knows Verify / Approve / Reject only (no Return).
export const buildApproveAdvancePayload = ({ advanceId, action, note, roleId, createdBy }) => {
    // getStatusSlice: action.type is the button kind (Approve / Verify / Reject); Value / Text vary per status list
    const raw = String(action?.type || action?.value || action?.text || '').trim().toLowerCase();
    const Action = raw.startsWith('reject') ? 'Reject' : raw.startsWith('approve') ? 'Approve' : 'Verify';
    return { AdvanceId: advanceId, Note: String(note || '').trim(), Action, RoleId: roleId || 0, CreatedBy: createdBy || '' };
};

// ── Advance CMS (LabourCMSPayCreation, Payment For = Advance) ──

// spSaveLabourAdvanceCMS / spGetLBAdvanceCMSGrid labour type literals (the Wages CMS uses 'Own' / 'Contractor')
export const advanceCMSLabourType = (uiLabourType) => (uiLabourType === 'Contractor Labour' ? 'Contractor' : 'Own Labour');

// Pay now: blank / 0 = not paid now; partial payment allowed up to the remaining balance.
export const advanceCMSRowError = (row, value) => {
    const s = String(value ?? '').trim();
    if (s === '') return '';
    const amt = Number(s);
    if (Number.isNaN(amt) || amt < 0) return 'Invalid amount';
    if (amt > (Number(row.Balance) || 0)) return 'More than balance';
    return '';
};

export const buildSaveAdvanceCMSPayload = ({ advanceId, uiLabourType, contractorCode, poNo, paymentDate, workerGrid, payNow, roleId, createdBy }) => {
    const contract = uiLabourType === 'Contractor Labour';
    return {
        AdvanceId:      advanceId,
        LabourType:     advanceCMSLabourType(uiLabourType),
        ContractorCode: contract ? (contractorCode || null) : null,
        PONo:           contract ? (poNo || null) : null,
        PaymentDate:    paymentDate,
        RoleId:         roleId || 0,
        CreatedBy:      createdBy || '',
        Workers: workerGrid
            .map((w) => ({ LabourId: w.LabourId, PayNow: Number(payNow[w.LabourId]) || 0 }))
            .filter((w) => w.PayNow > 0),
    };
};

// VerifyLabourCMSPay: LabourCMSHeader.PaymentFor is 'Wages' or 'Advance' (older rows have no value = Wages).
export const cmsPaymentFor = (row) => (String(row?.PaymentFor || '').trim().toLowerCase() === 'advance' ? 'Advance' : 'Wages');

// ── Labour Payroll Generation preview (spGetLabourPayrollStaging) ──
// Advance       = TrackedAdvance when there is one, else the sheet's Advance (ExcelAdvance)
// AdvanceBlocked = advance not yet paid through an approved Advance CMS -> spGenerateLabourPayroll refuses
//                  the whole payroll ("Advance not yet paid through an approved Labour CMS (Advance) for: ...").
export const payrollAdvanceInfo = (row) => ({
    tracked:    Number(row?.TrackedAdvance) || 0,
    excel:      Number(row?.ExcelAdvance) || 0,
    cmsPending: Number(row?.AdvanceCMSPending) || 0,
    blocked:    row?.AdvanceBlocked === true || row?.AdvanceBlocked === 1,
});

// The SP checks every approved labour in the sheet, not only 'Valid' rows - so does this.
export const payrollBlockedRows = (previewRows = []) =>
    previewRows.filter((r) => payrollAdvanceInfo(r).blocked);

// Labour CMS Payment Report (spCMSPayReportLBData): advance rows have PaymentFor 'Advance' and
// PayrollRefno 'Advance : <advance ref>' (no payroll). Returns the label + ref to show.
export const cmsReportRef = (rec) => {
    const raw = String(rec?.PayrollRefno || '');
    if (cmsPaymentFor(rec) === 'Advance') {
        return { label: 'Advance Ref', value: raw.replace(/^\s*Advance\s*:\s*/i, '') || 'N/A' };
    }
    return { label: 'Payroll', value: raw || 'N/A' };
};

// Wages / Advance split of report rows (summary cards)
export const cmsReportTotals = (rows = []) => rows.reduce((t, r) => {
    const amt = parseFloat(r.Amount) || 0;
    if (cmsPaymentFor(r) === 'Advance') t.advance += amt; else t.wages += amt;
    return t;
}, { wages: 0, advance: 0 });

// ── Labour Advance Report (spGetLabourAdvanceReport) ──

// Default range: April of the current financial year up to the current month.
export const defaultAdvanceReportRange = (today = new Date()) => {
    const m = today.getMonth() + 1, y = today.getFullYear();
    return { fromMonth: 4, fromYear: m >= 4 ? y : y - 1, toMonth: m, toYear: y };
};

export const advanceReportRangeError = ({ fromMonth, fromYear, toMonth, toYear }) => {
    if (!fromMonth || !fromYear || !toMonth || !toYear) return 'Select the from and to month';
    if (Number(fromYear) * 12 + Number(fromMonth) > Number(toYear) * 12 + Number(toMonth)) return 'From month is after the to month';
    return '';
};

// GetReport query: 'All' CC / labour type, empty labour id left out (backend default NULL)
export const buildAdvanceReportParams = (f, roleId, userId) => {
    const p = {
        ccCode: f.ccCode || 'All', fromMonth: Number(f.fromMonth), fromYear: Number(f.fromYear),
        toMonth: Number(f.toMonth), toYear: Number(f.toYear), labourType: f.labourType || 'All',
        onlyWithBalance: !!f.onlyWithBalance, roleId: roleId || 0, userId: userId || 0,
    };
    if (String(f.labourId || '').trim()) p.labourId = String(f.labourId).trim();
    return p;
};

// Lines (advance batches + payroll recoveries) behind one ledger row (Labour + CC)
export const advanceReportLinesFor = (lines = [], row) =>
    lines.filter((l) => l.LabourId === row.LabourId && l.CCCode === row.CCCode);

export const ADVANCE_REPORT_MONEY = ['Opening', 'AdvanceGiven', 'CMSPaid', 'Recovered', 'Closing'];
export const advanceReportTotals = (rows = []) =>
    ADVANCE_REPORT_MONEY.concat(['AdvancePending', 'RecoveryPending']).reduce((t, k) => {
        t[k] = rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
        return t;
    }, {});
